/**
 * POS-S6 minimal: elegir líneas a cobrar ahora; el resto queda en la mesa.
 * @vuelto-surface mobile:ok desktop:ok strategy:unified
 */
import React, { useMemo, useState } from 'react';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ModalShell } from '../../../shared/modals/ModalShell';
import { posSharedUi as posUi } from '../../../shared/ui/posSharedUi';
import type { CartItem } from '../../store/usePosStore';
import { formatPosMoney } from '../../application/posMoneyFormat';

type Props = {
  visible: boolean;
  lines: CartItem[];
  onConfirm: (selectedLineIds: string[]) => void;
  onClose: () => void;
};

export function SplitBillModal({ visible, lines, onConfirm, onClose }: Props) {
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  // Reset selection when opening
  React.useEffect(() => {
    if (!visible) return;
    const next: Record<string, boolean> = {};
    for (const line of lines) next[line.lineId] = false;
    setSelected(next);
  }, [visible, lines]);

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([id]) => id),
    [selected],
  );
  const canConfirm =
    selectedIds.length > 0 && selectedIds.length < lines.length;

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="Dividir cuenta"
      subtitle="Elegí qué cobrás ahora. El resto sigue en la mesa."
      keyboardAvoiding={false}
      widthVariant="compact"
      footer={
        <View style={{ flexDirection: 'row', gap: 8, flex: 1 }}>
          <TouchableOpacity
            style={[posUi.secondaryButton, { flex: 1 }]}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cancelar"
          >
            <Text style={posUi.secondaryButtonText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[posUi.primaryButton, { flex: 1 }, !canConfirm && { opacity: 0.5 }]}
            disabled={!canConfirm}
            onPress={() => onConfirm(selectedIds)}
            accessibilityRole="button"
            accessibilityLabel="Cobrar esta parte"
          >
            <Text style={posUi.primaryButtonText}>Cobrar esta parte</Text>
          </TouchableOpacity>
        </View>
      }
    >
      <ScrollView style={{ maxHeight: 360 }}>
        {lines.map((line) => {
          const checked = Boolean(selected[line.lineId]);
          const total = line.price * line.quantity;
          return (
            <TouchableOpacity
              key={line.lineId}
              onPress={() =>
                setSelected((prev) => ({ ...prev, [line.lineId]: !prev[line.lineId] }))
              }
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 10,
                borderBottomWidth: 1,
                borderBottomColor: ticketColors.ruleSoft,
                gap: 10,
              }}
              accessibilityRole="checkbox"
              accessibilityState={{ checked }}
              accessibilityLabel={line.name}
            >
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 4,
                  borderWidth: 2,
                  borderColor: checked ? ticketColors.gold : ticketColors.rule,
                  backgroundColor: checked ? ticketColors.gold : 'transparent',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {checked ? (
                  <Text style={{ color: ticketColors.goldInk, fontSize: 14, fontWeight: '700' }}>✓</Text>
                ) : null}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 15, fontWeight: '600', color: ticketColors.ink }}>
                  {line.quantity}× {line.name}
                </Text>
              </View>
              <Text style={{ fontSize: 14, color: ticketColors.ink2 }}>
                {formatPosMoney(total)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
      {!canConfirm ? (
        <Text style={{ marginTop: 8, fontSize: 13, color: ticketColors.ink3 }}>
          Marcá al menos una línea y dejá otra sin marcar.
        </Text>
      ) : null}
    </ModalShell>
  );
}
